import PropTypes from 'prop-types';
import { useState, useEffect, useRef } from 'react';
import { createRefetchContainer, graphql, useRelayEnvironment } from 'react-relay';
import { BarHeader } from 'dibs-elements/exports/BarHeader';
import { ModalBody } from 'dibs-elements/exports/ModalBody';
import { getUserGeoInfo, setUserGeoInfo } from 'dibs-regional-info/exports/regionalInfoHelpers';
import CustomsMessaging from 'dibs-customs-messaging/exports/CustomsMessaging';
import ShippingDetailsQuotesTable from './ShippingDetailsQuotes/ShippingDetailsQuotesTable';
import { ShippingDetailsRequestQuote } from './ShippingDetailsText/ShippingDetailsRequestQuote';
import { ShippingDetailsServicesTable } from './ShippingDetailsServices/ShippingDetailsServicesTable';
import { shippingModal } from './shippingMessages';
import ShippingDetailsBuyerLocationForm from './ShippingDetailsBuyerLocationForm/ShippingDetailsBuyerLocationForm';
import styles from './ShippingDetailsContainer.scss';
import { forceSetUserRegionalInfo } from 'dibs-regional-info/exports/setUserRegionalInfo';
import serverVars from 'server-vars';

export function ShippingDetailsContainerComponent(props) {
    const {
        currency,
        viewer,
        openQuoteRequestModal,
        quoteRequestStatus,
        blockRequest,
        onPreQuotesLoadComplete,
        relay,
        itemQuantity,
    } = props;
    const relayEnv = useRelayEnvironment();
    const initialLoadRef = useRef(false);
    const { item, user } = viewer || {};
    const { lastTransaction, serviceId: itemId } = item || {};
    const lastTransactionId = lastTransaction?.serviceId || '';
    const { addressBook, serviceId: userId } = user || {};
    const { proAddress, personalAddress, addressList } = addressBook || {};
    const shipToAddress = proAddress || personalAddress || addressList?.[0];

    const { countryCode, zipCode: regionalZipCode } = getUserGeoInfo() || {};
    const initialCountry = shipToAddress?.country || countryCode || '';
    const initialZipCode =
        (shipToAddress?.country ? shipToAddress?.zipCode : regionalZipCode) || '';

    const [submittedBuyerAddress, setSubmittedBuyerAddress] = useState({
        country: initialCountry,
        zipCode: initialZipCode,
    });
    const [isPrequotesLoading, setIsPrequotesLoading] = useState(false);

    useEffect(() => {
        if (!initialLoadRef.current) {
            setIsPrequotesLoading(true);
            relay.refetch(
                {
                    itemId: itemId || '',
                    transactionId: lastTransactionId,
                    country: initialCountry,
                    zipCode: initialZipCode,
                    shouldFetchTransactionDelivery: !!lastTransactionId && !!initialZipCode,
                    shouldFetchItemDelivery: !!itemId,
                    addressResolutionType: initialCountry ? 'ADDRESS' : 'IP',
                    itemQuantity,
                },
                null,
                () => {
                    setIsPrequotesLoading(false);
                    initialLoadRef.current = true;
                }
            );
        }
    }, [itemId, lastTransactionId, relay, initialZipCode, initialCountry, itemQuantity]);

    const onAddressSubmit = ({ country, zipCode }) => {
        setSubmittedBuyerAddress({ country, zipCode });
        setIsPrequotesLoading(true);
        relay.refetch(
            {
                itemId: itemId || '',
                transactionId: lastTransactionId,
                country: country,
                zipCode: zipCode,
                shouldFetchTransactionDelivery: !!lastTransactionId && !!zipCode,
                shouldFetchItemDelivery: !!itemId,
                addressResolutionType: country ? 'ADDRESS' : 'IP',
                itemQuantity,
            },
            null,
            () => {
                setIsPrequotesLoading(false);
                onPreQuotesLoadComplete({
                    userZipCode: zipCode,
                    userCountryCode: country,
                });
            }
        );

        if (userId) {
            forceSetUserRegionalInfo({
                relayEnv,
                cookieDomain: serverVars.get('cookieDomain'),
                userId: userId || '',
                useLocalStorage: true,
            });
        } else {
            setUserGeoInfo({
                userGeoInfo: {
                    countryCode: country,
                    zipCode: zipCode,
                    incomeLevel: null,
                    regionsByZipCode: null,
                },
                environment: relayEnv,
                useLocalStorage: true,
            });
        }
    };

    if (!viewer) {
        return null;
    }
    return (
        <div className={styles.wrapper}>
            <BarHeader title={shippingModal.modalTitle} />
            <ModalBody>
                <ShippingDetailsBuyerLocationForm
                    viewer={viewer}
                    item={item}
                    location={submittedBuyerAddress}
                    userId={userId || ''}
                    onAddressSubmit={onAddressSubmit}
                    isDisabled={isPrequotesLoading}
                />
                <ShippingDetailsQuotesTable
                    currency={currency}
                    viewer={viewer}
                    item={item}
                    country={submittedBuyerAddress.country}
                    zipCode={submittedBuyerAddress.zipCode}
                    quoteRequestStatus={quoteRequestStatus}
                    openQuoteRequestModal={openQuoteRequestModal}
                    blockRequest={blockRequest}
                    isLoading={isPrequotesLoading}
                />
                {/* request quote text */}
                <ShippingDetailsRequestQuote
                    quoteRequestStatus={quoteRequestStatus}
                    openQuoteRequestModal={openQuoteRequestModal}
                    blockRequest={blockRequest}
                />
                {/* services table */}
                <ShippingDetailsServicesTable />

                <CustomsMessaging
                    environment={relayEnv}
                    userId={user?.serviceId}
                    itemId={item?.serviceId}
                    currency={currency}
                />
            </ModalBody>
        </div>
    );
}

ShippingDetailsContainerComponent.propTypes = {
    viewer: PropTypes.object,
    currency: PropTypes.string,
    openQuoteRequestModal: PropTypes.func,
    quoteRequestStatus: PropTypes.string.isRequired,
    blockRequest: PropTypes.bool,
    onPreQuotesLoadComplete: PropTypes.func,
    relay: PropTypes.object,
    itemQuantity: PropTypes.number,
};

export const ShippingDetailsContainer = createRefetchContainer(
    ShippingDetailsContainerComponent,
    {
        viewer: graphql`
            fragment ShippingDetailsContainer_viewer on Viewer {
                ...ShippingDetailsQuotesTable_viewer
                ...ShippingDetailsBuyerLocationForm_viewer
                item(itemId: $itemId, pageType: SHIPPING_MODAL) @include(if: $shouldFetchItem) {
                    serviceId
                    lastTransaction(buyerId: $userId) {
                        serviceId
                    }
                    ...ShippingDetailsQuotesTable_item
                    ...ShippingDetailsBuyerLocationForm_item
                }
                user(userId: $userId) @include(if: $hasUserId) {
                    serviceId
                    addressBook {
                        addressList {
                            serviceId
                            country
                            zipCode
                        }
                        proAddress {
                            serviceId
                            country
                            zipCode
                        }
                        personalAddress {
                            serviceId
                            country
                            zipCode
                        }
                    }
                    ...clientAbTestV2_useAbTestV2BuyerUser_user
                }
                regionalInfo {
                    countryCode
                    zipCode
                }
            }
        `,
    },
    graphql`
        query ShippingDetailsContainerRefetchQuery(
            $shouldFetchTransactionDelivery: Boolean = false
            $shouldFetchItemDelivery: Boolean = false
            $country: String = ""
            $zipCode: String = ""
            $transactionId: String = ""
            $itemId: String = ""
            $addressResolutionType: AddressResolutionType = IP
            $itemQuantity: Int
            $userId: String = ""
        ) {
            viewer {
                ...ShippingDetailsQuotesTable_viewer
            }
        }
    `
);
